import { ReactNode } from "react";
import { CustomLabels, HeaderBreadcrumbs } from "./HeaderBreadcrumbs";
import { usePreload } from "./PreloadProvider";
import { useSidebar } from "@/components/ui/sidebar";
import { MenuIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useScrollRestoration } from "./ScrollRestoration";

interface IProps {
    customLabels?: CustomLabels;
    breadcrumbReplacement?: ReactNode;
    noSpacing?: boolean;
    mainClassName?: string;
    className?: string;
    children: ReactNode;
}

export const PageFrame = ({
    customLabels,
    breadcrumbReplacement,
    noSpacing,
    className,
    mainClassName,
    children
}: IProps) => {
    const { isPreloading } = usePreload();

    useScrollRestoration({ containerId: "root", root: true });
    const { ref, onScroll } = useScrollRestoration({
        containerId: "PageFrame"
    });

    return (
        <div className="md-not-print:absolute md-not-print:inset-0 grow flex flex-col">
            <div className="sticky top-0 md:static z-10 w-full bg-background pb-2 md:pb-4 print:hidden">
                <header className="flex pt-1 pl-2 md:pt-4 md:pl-4 pr-4 gap-1">
                    {!isPreloading && <ToggleSidebarButton />}
                    <div className="pt-2 md:pt-0">
                        {breadcrumbReplacement ? (
                            breadcrumbReplacement
                        ) : (
                            <HeaderBreadcrumbs customLabels={customLabels} />
                        )}
                    </div>
                </header>
            </div>
            <main
                className={cn(
                    "z-0 grow relative md-not-print:overflow-auto",
                    mainClassName
                )}
                ref={ref}
                onScroll={onScroll}
            >
                <div
                    className={cn(
                        "mx-auto pt-2 pb-6 md-not-print:px-4 max-w-4xl",
                        !noSpacing && "space-y-6",
                        className
                    )}
                >
                    {children}
                </div>
            </main>
        </div>
    );
};

const ToggleSidebarButton = () => {
    const { toggleSidebar } = useSidebar();

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="flex h-7 items-end pb-1 md:hidden"
        >
            <MenuIcon />
            <span className="sr-only">Toggle sidebar</span>
        </Button>
    );
};
